import * as React from "react";
import { formatMessage } from "lib/globalize-util";
import { DATATEST } from "constants/integration";
import { Export } from "./export";
import Button from "components/form/button";
import CircularProgress from "material-ui/CircularProgress";
import { AlertDialog } from "components/dialog/alert";
import exportStyles, { styles } from "./export-styles";
import {
    ALLOCATION_DIMENSION_ID_PREFIX,
    EXPORT_STATUS,
    ExportCollectionSelectedSetting,
} from "constants/product-collection";
import {
    AllocationDimensionKind,
    IAllocationDimension,
    ROLLUP_ALLOCATION_DIMENSION_DELIMITER,
} from "helpers/allocation-dimension";
import { Checkbox, FormControlLabel, FormHelperText } from "@material-ui/core";
import Label from "components/form/label";
import { uniqBy } from "lodash";
import { RadioButtonGroup, RadioButton } from "material-ui";
import { GridApi } from "ag-grid-community";
import { IDoorTotals } from "reducers/collection";

interface IProps {
    onCancel: () => void;
    onConfirm: (config) => void;
    collectionExport: any;
    allocationDimensions: IAllocationDimension[];
    gridApi: GridApi;
    doorTotals: IDoorTotals;
    doorTotalsFiltered: IDoorTotals;
}

export const confirmExportCallback = (
    dimensionIds: string[],
    patternsToClear: (string | RegExp)[],
    onConfirm: IProps["onConfirm"],
    exportOnlyVisibleRows: ExportCollectionSelectedSetting,
    exportOnlyVisibleColumns: ExportCollectionSelectedSetting,
    withImages
) =>
    onConfirm({
        selectedAllocationDimensions: dimensionIds.map((id) =>
            patternsToClear.reduce((acc: string, pattern) => acc.replace(pattern, ""), id)
        ),
        exportOnlyVisibleRows,
        exportOnlyVisibleColumns,
        withImages,
    });

const IMAGE_EXPORT_LIMIT = 10000;
export const uniqueAllocationDimensionsWithoutPattern = (
    allocationDimensions: IAllocationDimension[],
    pattern: string | RegExp
) => uniqBy(allocationDimensions, (dim) => dim.id.replace(pattern, ""));
export const ExportRollup: React.FunctionComponent<IProps> = ({
    onCancel,
    onConfirm,
    collectionExport,
    allocationDimensions,
    gridApi,
    doorTotals,
    doorTotalsFiltered,
}) => {
    const assortmentIdentifierRegExp = React.useMemo(
        () => new RegExp(`${ROLLUP_ALLOCATION_DIMENSION_DELIMITER}.+`),
        []
    );
    const [selectedDimensionsIds, setSelectedDimensionsIds] = React.useState<string[]>([]);
    const [exportOnlyVisibleRows, setExportOnlyVisibleRows] = React.useState(ExportCollectionSelectedSetting.Selected);
    const [exportOnlyVisibleColumns, setExportOnlyVisibleColumns] = React.useState(
        ExportCollectionSelectedSetting.Visible
    );
    const [withImages, setWithImages] = React.useState<boolean>(false);

    const getNumberOfRows = ({ rowExportSelection }) => {
        if (rowExportSelection === ExportCollectionSelectedSetting.All) {
            return doorTotals?._total?.totalRows;
        }
        if (rowExportSelection === ExportCollectionSelectedSetting.Visible) {
            return doorTotalsFiltered?._total?.totalRows;
        }
        if (rowExportSelection === ExportCollectionSelectedSetting.Selected) {
            const rowCount = gridApi.getSelectedNodes().reduce((acc) => acc + 1, 0);
            return rowCount;
        }
    };
    const handleChangeVisibleRows = React.useCallback((_event, value) => {
        setExportOnlyVisibleRows(value);
        if (getNumberOfRows({ rowExportSelection: value }) > IMAGE_EXPORT_LIMIT) {
            setWithImages(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handleChangeVisibleColumns = React.useCallback((_event, value) => {
        setExportOnlyVisibleColumns(value);
    }, []);
    React.useEffect(() => {
        if (gridApi.getSelectedRows().length === 0) {
            setExportOnlyVisibleRows(ExportCollectionSelectedSetting.All);
        }
        getNumberOfRows({ rowExportSelection: exportOnlyVisibleRows });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gridApi, setExportOnlyVisibleRows]);

    const handleConfirmExport = React.useCallback(
        () =>
            confirmExportCallback(
                selectedDimensionsIds,
                [ALLOCATION_DIMENSION_ID_PREFIX, assortmentIdentifierRegExp],
                onConfirm,
                exportOnlyVisibleRows,
                exportOnlyVisibleColumns,
                withImages
            ),
        [
            selectedDimensionsIds,
            assortmentIdentifierRegExp,
            onConfirm,
            exportOnlyVisibleRows,
            exportOnlyVisibleColumns,
            withImages,
        ]
    );
    const handleDimensionsChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
            const allocationDimensionId = event.target.value;
            const filteredIds = selectedDimensionsIds.filter((id) => id !== allocationDimensionId);
            if (checked) {
                setSelectedDimensionsIds(filteredIds.concat([allocationDimensionId]));
            } else {
                setSelectedDimensionsIds(filteredIds);
            }
        },
        [setSelectedDimensionsIds, selectedDimensionsIds]
    );
    const allocationDimensionsValue = React.useMemo(
        () =>
            allocationDimensions.length > 0 &&
            uniqueAllocationDimensionsWithoutPattern(
                allocationDimensions.filter((d) => d.subtype !== AllocationDimensionKind.Size),
                assortmentIdentifierRegExp
            ).map((allocationDimension, i) => {
                return (
                    <FormControlLabel
                        key={i}
                        control={
                            <Checkbox
                                checked={selectedDimensionsIds.includes(allocationDimension.id)}
                                onChange={handleDimensionsChange}
                                name={allocationDimension.name}
                                className="checkbox"
                                value={allocationDimension.id}
                                data-test={`${DATATEST.ROLLUP.EXPORT_ALLOCATION_DIMENSION}-${allocationDimension.name}`}
                            />
                        }
                        label={allocationDimension.name}
                    />
                );
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [allocationDimensions, selectedDimensionsIds, handleDimensionsChange]
    );

    const exceedImageExportLimit = React.useMemo(() => {
        const rowCount = getNumberOfRows({ rowExportSelection: exportOnlyVisibleRows });
        return rowCount > IMAGE_EXPORT_LIMIT;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [exportOnlyVisibleRows]);

    return (
        <AlertDialog
            dismissButton={false}
            onRequestClose={onCancel}
            open={true}
            title={formatMessage("Export Rollup")}
            contentStyle={{ maxWidth: "600px" }}
        >
            <Export
                error={collectionExport.get("error") || collectionExport.get("status") === EXPORT_STATUS.FAILED}
                isLoading={!collectionExport.get("error") && collectionExport.get("status") === EXPORT_STATUS.CREATED}
                isReady={!collectionExport.get("error") && collectionExport.get("status") === EXPORT_STATUS.FINISHED}
            >
                <Export.Selection>
                    <div {...exportStyles}>
                        <div className="formGroupContainer">
                            <p data-test={DATATEST.ROLLUP.EXPORT_DIALOG_DESCRIPTION}>
                                {formatMessage("Your export will include all rows and columns.")}
                            </p>
                        </div>
                        <div className="formGroupContainer">
                            <div>
                                <Label style={styles.label}>{formatMessage("Rows")}</Label>
                                <RadioButtonGroup
                                    name="rows"
                                    valueSelected={exportOnlyVisibleRows}
                                    onChange={handleChangeVisibleRows}
                                >
                                    <RadioButton
                                        disabled={gridApi.getSelectedRows().length === 0}
                                        label={formatMessage("Selected Rows Only")}
                                        value={ExportCollectionSelectedSetting.Selected}
                                    />
                                    <RadioButton
                                        label={formatMessage("Filtered Rows Only")}
                                        value={ExportCollectionSelectedSetting.Visible}
                                    />
                                    <RadioButton
                                        label={formatMessage("All Rows")}
                                        value={ExportCollectionSelectedSetting.All}
                                        data-test={DATATEST.ROLLUP.ALL_ROWS}
                                    />
                                </RadioButtonGroup>
                            </div>
                            <div>
                                <Label style={styles.label}>{formatMessage("Columns")}</Label>
                                <RadioButtonGroup
                                    name="columns"
                                    valueSelected={exportOnlyVisibleColumns}
                                    onChange={handleChangeVisibleColumns}
                                >
                                    <RadioButton
                                        data-test={DATATEST.ROLLUP.SELECT_ALL}
                                        label={formatMessage("Selected Columns Only")}
                                        value={ExportCollectionSelectedSetting.Visible}
                                    />
                                    <RadioButton
                                        data-test={DATATEST.ROLLUP.VISIBLE}
                                        label={formatMessage("All Columns")}
                                        value={ExportCollectionSelectedSetting.All}
                                    />
                                </RadioButtonGroup>
                            </div>
                        </div>
                        <div className="formGroupContainer">
                            {allocationDimensions.length > 0 && (
                                <div>
                                    <Label style={styles.label}>{formatMessage("Include units for")}</Label>
                                    {allocationDimensionsValue}
                                </div>
                            )}
                        </div>
                        <div className="bottomRow">
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={withImages}
                                        onChange={() => setWithImages(!withImages)}
                                        className="checkbox"
                                    />
                                }
                                label={formatMessage("Include images")}
                            />
                            {exceedImageExportLimit && withImages && (
                                <FormHelperText
                                    style={{ color: "rgb(245, 78, 66)", fontSize: "12px", marginLeft: "10px" }}
                                >
                                    {formatMessage("10,000 product row maximum to export images")}
                                </FormHelperText>
                            )}
                        </div>
                        <div className="buttonContainer">
                            <Button
                                data-test={DATATEST.ROLLUP.EXPORT_DIALOG_BUTTON}
                                label={formatMessage("Export")}
                                primary={true}
                                disabled={exceedImageExportLimit && withImages}
                                onClick={handleConfirmExport}
                            />
                        </div>
                    </div>
                </Export.Selection>
                <Export.Loading>
                    <div {...exportStyles} className="loadingExport">
                        <CircularProgress />
                        <p>{formatMessage("Please wait as we prepare your rollup")}</p>
                    </div>
                </Export.Loading>
                <Export.Ready>
                    <a {...exportStyles} className="downloadExport" href={collectionExport.get("object_url")}>
                        <Button label={formatMessage("Download")} primary={true} large={true} />
                    </a>
                </Export.Ready>
                <Export.Error>
                    <p>{formatMessage("There was an error exporting your rollup.")}</p>
                </Export.Error>
            </Export>
        </AlertDialog>
    );
};
